import { useMemo, useCallback } from "react"
import PropTypes from "prop-types"
import { chain, partition, noop } from "lodash"
import SearchableMenu from "components/searchable-menu"
import { useIsOpPro, useAccountAlias } from "resources/account"
import { CONTENT_SET_METADATA } from "resources/content-sets"
import { useAnalytics } from "connected-components/analytics"
import { useMetricsSelectorAnalytics } from "components/custom-metrics-selector/hooks/use-metrics-selector-analytics"
import { useTrackDashboardModuleAdded } from "connected-components/dashboard/analytics"
import { useAddModule } from "connected-components/dashboard/hooks/use-add-module"
import {
  buildSecondaryModuleConfigFromMenuItem,
  SECONDARY_MODULE_CONFIG_VIEW_KEYS,
} from "connected-components/dashboard/helpers/module-renderers"
import { MODULE_TYPES } from "connected-components/dashboard/modules/types"
import { useAssetViewData } from "connected-components/asset-view/hooks/use-asset-view-data"
import {
  beginNewCustomView,
  resetDuplicateFlowOnViewSelect,
} from "connected-components/asset-view/helpers/duplicate-view-flow"
import { useDuplicateFlow } from "connected-components/asset-view/hooks/use-duplicate-flow"
import { buildCustomTemplateMenuActions } from "./build-custom-template-menu-actions"
import {
  GROUP_IDS,
  GROUP_LABELS,
  buildDefaultViewItems,
  buildContentSetViewItems,
  getOpenInNewTabMergeOptions,
  mergeItemsWithOrderAndSecondaryAction,
} from "./assets-menu-items"
import { ASSET_VIEW_EVENTS } from "connected-components/asset-view/analytics/events"

import "./style.scss"

export default function AssetsViewSelector({ templates, contentSets }) {
  const assetViewContext = useAssetViewData()
  const { view, setView, setShowMetricsSelector, clearDuplicateState, deleteTemplate, dateOverride } = assetViewContext
  const addModule = useAddModule()
  const analytics = useAnalytics()
  const trackModuleAdded = useTrackDashboardModuleAdded()
  const metricsSelectorAnalytics = useMetricsSelectorAnalytics(assetViewContext)

  const isOpPro = useIsOpPro()
  const account = useAccountAlias()
  const [proprietaryContentSets, licensedContentSets] = useMemo(() => partition(contentSets, "proprietary"), [contentSets])

  const duplicateFlow = useDuplicateFlow()

  const defaultMenuItems = useMemo(() => buildDefaultViewItems(isOpPro), [isOpPro])

  const contentSetMenuItems = useMemo(
    () => buildContentSetViewItems(licensedContentSets, proprietaryContentSets),
    [licensedContentSets, proprietaryContentSets],
  )

  const trackAnalytics = analytics?.track ?? noop

  const customMenuItems = useMemo(() => [
    {
      id: "custom",
      label: "New View...",
      groupId: GROUP_IDS.CUSTOM,
      groupLabel: GROUP_LABELS.CUSTOM,
      onSelect: () => {
        metricsSelectorAnalytics.trackOpened()
        beginNewCustomView({ clearDuplicateState, setView, setShowMetricsSelector })
      },
    },
    ...chain(templates)
      .sortBy("label")
      .map((template) => {
        const actions = buildCustomTemplateMenuActions(
          account,
          template,
          duplicateFlow,
          deleteTemplate,
          trackAnalytics,
        )
        return {
          id: template.id,
          label: template.name,
          groupId: GROUP_IDS.CUSTOM,
          groupLabel: GROUP_LABELS.CUSTOM,
          ...(actions.length > 0 && { actions }),
        }
      })
      .value(),
  ], [
    setView,
    setShowMetricsSelector,
    duplicateFlow,
    templates,
    deleteTemplate,
    account,
    metricsSelectorAnalytics,
    trackAnalytics,
    clearDuplicateState,
  ])

  const handleOpenInNewTab = useCallback((item, event) => {
    analytics.track(ASSET_VIEW_EVENTS.TAB_SELECTED, { selectedTab: item.id })
    const baseConfig = buildSecondaryModuleConfigFromMenuItem(item, event, SECONDARY_MODULE_CONFIG_VIEW_KEYS.INITIAL_VIEW)
    // Carry the current panel's dateOverride forward, mirroring KPI/graph-template-selector.
    const config = { ...baseConfig, dateOverride }
    addModule({
      type: MODULE_TYPES.ASSET_VIEW,
      config,
    })
    trackModuleAdded(MODULE_TYPES.ASSET_VIEW, config)
  }, [analytics, addModule, trackModuleAdded, dateOverride])

  const menuItems = useMemo(() => mergeItemsWithOrderAndSecondaryAction(
    [customMenuItems, defaultMenuItems, contentSetMenuItems],
    getOpenInNewTabMergeOptions(handleOpenInNewTab),
  ), [customMenuItems, defaultMenuItems, contentSetMenuItems, handleOpenInNewTab])

  const handleSelect = useCallback((selectedItem) => {
    analytics.track(ASSET_VIEW_EVENTS.TAB_SELECTED, { selectedTab: selectedItem.id })
    if (selectedItem.onSelect) {
      selectedItem.onSelect()
      return
    }
    resetDuplicateFlowOnViewSelect({ clearDuplicateState, setShowMetricsSelector })
    setView(selectedItem.id)
  }, [analytics, setView, clearDuplicateState, setShowMetricsSelector])

  return <SearchableMenu items={menuItems} current={view} onSelect={handleSelect} />
}

AssetsViewSelector.propTypes = {
  templates: PropTypes.array.isRequired,
  contentSets: PropTypes.arrayOf(CONTENT_SET_METADATA).isRequired,
}
